import React, { useState } from 'react';
import ReactDOM from 'react-dom';

interface EventTooltipProps {
  children: React.ReactNode;
  content: {
    title: string;
    course?: string;
    teacher?: string;
    location?: string;
    room?: string;
    time?: string;
  };
  isDarkMode: boolean;
}

const EventTooltip: React.FC<EventTooltipProps> = ({ children, content, isDarkMode }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent) => {
    // Follow cursor position with small offset
    setMousePos({ 
      x: e.clientX, 
      y: e.clientY
    });
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    setMousePos({ 
      x: e.clientX, 
      y: e.clientY
    });
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  const tooltipContent = isVisible && (
    <div
      style={{
        position: 'fixed',
        top: `${mousePos.y - 10}px`,
        left: `${mousePos.x + 15}px`,
        transform: 'translateY(-100%)',
        backgroundColor: isDarkMode ? '#424242' : '#333333',
        color: '#ffffff',
        padding: '8px 12px',
        borderRadius: '6px',
        fontSize: '13px',
        lineHeight: '1.4',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
        zIndex: 999999,
        pointerEvents: 'none',
        maxWidth: '250px',
        minWidth: '150px',
        animation: 'fadeIn 0.2s ease-in',
      }}
    >
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-90%); }
            to { opacity: 1; transform: translateY(-100%); }
          }
        `}
      </style>
      <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '14px' }}>
        {content.title}
      </div>
      {content.time && (
        <div style={{ marginBottom: '4px', opacity: 0.9 }}>
          🕐 {content.time}
        </div>
      )}
      {content.course && (
        <div style={{ marginBottom: '2px', opacity: 0.9 }}>
          📚 {content.course}
        </div>
      )}
      {content.teacher && (
        <div style={{ marginBottom: '2px', opacity: 0.9 }}>
          👨‍🏫 {content.teacher}
        </div>
      )}
      {content.location && (
        <div style={{ marginBottom: '2px', opacity: 0.9 }}>
          📍 {content.location}
        </div>
      )}
      {content.room && (
        <div style={{ opacity: 0.9 }}>
          🚪 {content.room}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ height: '100%', width: '100%', cursor: 'pointer' }}
      >
        {children}
      </div>
      {/* Use React Portal to render tooltip at document body level */}
      {isVisible && ReactDOM.createPortal(tooltipContent, document.body)}
    </>
  );
};

export default EventTooltip;